import { ArrowRight, Loader2, PencilRuler, Sparkles, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { GenerateLessonPackDialog } from '@/components/lesson-packs/GenerateLessonPackDialog';
import {
  CurriculumPicker,
  type CurriculumSelection,
} from '@/components/lesson-packs/CurriculumPicker';
import { AppShell } from '@/components/landing/AppShell';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import type { DesignSystemSummary, LessonPackInput } from '@/lib/schema.types';

const INITIAL_SELECTION: CurriculumSelection = {
  board: 'CBSE',
  classNum: null,
  grade: '',
  subjectRaw: '',
  subject: 'general',
  chapter: '',
  topics: [],
  chapterDoc: null,
};

export default function NewLessonPackPage() {
  const navigate = useNavigate();

  const [selection, setSelection] = useState<CurriculumSelection>(INITIAL_SELECTION);

  const [lessonScope, setLessonScope] = useState('');
  const [referenceText, setReferenceText] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  // Authoring pipeline. Defaults to the stable single-shot engine; the backend
  // falls back to 'v6.3' too, so this stays a safe choice if left untouched.
  const [pipelineVersion, setPipelineVersion] = useState<'v6.3' | 'v6.4'>('v6.3');

  // Design Repository: optional visual identity for the generated slides.
  // '' = default look (no design system).
  const [designSystemId, setDesignSystemId] = useState('');
  const { data: designSystems } = useSWR<DesignSystemSummary[]>(
    '/design-systems',
    () => api.designSystems.list(),
  );

  const [dialogOpen, setDialogOpen] = useState(false);

  // Start-from-scratch (no AI): create an empty pack and jump into the editor.
  const [blankTitle, setBlankTitle] = useState('');
  const [blankBusy, setBlankBusy] = useState(false);
  const [blankError, setBlankError] = useState<string | null>(null);

  // Phase-2 RAG: when the picked topic(s) carry curriculum `content`, use it as
  // the reference text automatically. The admin's own typed reference (if any)
  // always wins; this only fills the gap.
  const curriculumReference = useMemo(() => {
    const docTopics = selection.chapterDoc?.topics ?? [];
    const picked = new Set(selection.topics);
    return docTopics
      .filter((t) => picked.has(t.title) && t.content && t.content.trim())
      .map((t) => `## ${t.title}\n${t.content!.trim()}`)
      .join('\n\n');
  }, [selection.chapterDoc, selection.topics]);

  const input = useMemo<LessonPackInput>(() => {
    const topics = selection.topics.map((t) => t.trim()).filter(Boolean);
    return {
      board: selection.board,
      grade: selection.grade,
      subject: selection.subject,
      chapter: selection.chapter.trim(),
      // `topic` mirrors the first entry for back-compat; `topics` is canonical.
      topic: topics[0] ?? '',
      topics,
      lessonScope: lessonScope.trim() || undefined,
      // Typed reference wins; otherwise fall back to the curriculum content.
      referenceText: referenceText.trim() || curriculumReference || undefined,
      additionalNotes: additionalNotes.trim() || undefined,
      pipelineVersion,
      designSystemId: designSystemId || undefined,
    };
  }, [selection, lessonScope, referenceText, additionalNotes, pipelineVersion, curriculumReference, designSystemId]);

  const canSubmit =
    !!selection.board &&
    !!selection.grade &&
    !!selection.subject &&
    input.chapter.length > 0 &&
    input.topics.length > 0;

  // Blank packs only need identity metadata (board/class/subject) + a title —
  // no chapter/topics, since there's nothing to generate from.
  const canCreateBlank =
    !!selection.board && !!selection.grade && !!selection.subject && !!blankTitle.trim();

  const createBlank = async () => {
    if (!canCreateBlank || blankBusy) return;
    setBlankBusy(true);
    setBlankError(null);
    try {
      const res = await api.lessonPacks.createBlank({
        title: blankTitle.trim(),
        board: selection.board,
        grade: selection.grade,
        subject: selection.subject,
        chapter: selection.chapter.trim() || undefined,
        designSystemId: designSystemId || undefined,
      });
      navigate(`/lesson-packs/${res.id}`);
    } catch (err) {
      setBlankError(err instanceof Error ? err.message : 'Could not create the pack.');
      setBlankBusy(false);
    }
  };

  return (
    <AppShell
      activeNav="lesson-packs"
      showSearch={false}
      topBarLeader={
        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-fg">
          Lesson Pack Studio
        </span>
      }
      tabs={[
        { id: 'all', label: 'All packs', href: '/lesson-packs' },
        { id: 'new', label: 'New', active: true },
      ]}
      showProfile={false}
    >
      <div className="relative flex min-h-[calc(100vh-56px)] items-start justify-center px-6 pt-12 pb-24">
        <div className="w-full max-w-[860px]">
          <div className="relative grid grid-cols-1 overflow-hidden rounded-2xl border border-border bg-bg-panel shadow-slide md:grid-cols-[300px_1fr]">
            <Link
              to="/lesson-packs"
              title="Close"
              className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md text-fg-subtle hover:bg-bg-subtle hover:text-fg"
            >
              <X size={16} />
            </Link>

            <aside className="hidden flex-col justify-between bg-accent-soft/50 px-6 py-7 md:flex">
              <div>
                <span className="inline-flex items-center rounded-md bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
                  AI Generated
                </span>
                <h2 className="mt-3 text-[19px] font-semibold leading-tight text-fg">
                  New Lesson Pack
                </h2>
                <p className="mt-2 text-[12.5px] leading-snug text-fg-muted">
                  Pick from the curriculum catalogue (or type manually).
                </p>
              </div>
              <ul className="mt-8 space-y-2 text-[11.5px] text-fg-muted">
                <li>1. Pick class · subject · chapter · topic</li>
                <li>2. Preview or edit the generated slides</li>
                <li>3. Publish for the classroom app</li>
              </ul>
            </aside>

            <section className="flex flex-col px-7 py-7">
              <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-accent">
                <Sparkles size={14} />
                Pack Configuration
              </div>

              <div className="mt-5">
                <CurriculumPicker value={selection} onChange={setSelection} />
              </div>

              <div className="mt-5 space-y-4">
                <Field label="Lesson scope (optional)">
                  <input
                    value={lessonScope}
                    onChange={(e) => setLessonScope(e.target.value)}
                    placeholder="e.g. Traditional and modern irrigation methods"
                    className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                </Field>

                <Field
                  label="Authoring engine"
                  hint="v6.3 is the stable single-shot prompt. v6.4 runs the newer 3-pass pipeline (outline → expand → stitch); pick it to try the updated prompts."
                >
                  <select
                    value={pipelineVersion}
                    onChange={(e) =>
                      setPipelineVersion(e.target.value as 'v6.3' | 'v6.4')
                    }
                    className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  >
                    <option value="v6.3">v6.3 — Stable (single-shot)</option>
                    <option value="v6.4">v6.4 — 3-pass (outline → expand → stitch)</option>
                  </select>
                </Field>

                <Field
                  label="Design system (optional)"
                  hint="Pick a visual identity from the Design Repository — palette, typography and assets used to style the generated slides."
                >
                  <select
                    value={designSystemId}
                    onChange={(e) => setDesignSystemId(e.target.value)}
                    className="h-10 w-full rounded-md border border-border bg-white px-3 text-[13.5px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  >
                    <option value="">Default look (no design system)</option>
                    {(designSystems ?? []).map((ds) => (
                      <option key={ds._id} value={ds._id}>
                        {ds.name}
                        {ds.assetCount ? ` — ${ds.assetCount} assets` : ''}
                      </option>
                    ))}
                  </select>
                  {designSystemId && (
                    <DesignSwatchStrip
                      ds={(designSystems ?? []).find((d) => d._id === designSystemId)}
                    />
                  )}
                </Field>

                <Field
                  label="Reference text (optional)"
                  hint="Paste the NCERT chapter text or other primary source. If the selected topic has curriculum content, it's used automatically — leave this empty to use it, or type here to override."
                >
                  <textarea
                    rows={6}
                    value={referenceText}
                    onChange={(e) => setReferenceText(e.target.value)}
                    placeholder={
                      curriculumReference
                        ? 'Using the curriculum content for the selected topic(s). Type here to override…'
                        : 'Paste chapter text here…'
                    }
                    className="w-full resize-y rounded-md border border-border bg-white px-3 py-2 text-[13px] text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                  {curriculumReference && !referenceText.trim() && (
                    <span className="mt-1 block text-[11px] font-medium text-emerald-600">
                      ✓ Using curriculum content for the selected topic(s) (
                      {curriculumReference.length.toLocaleString()} chars).
                    </span>
                  )}
                </Field>

                <Field label="Additional notes (optional)">
                  <textarea
                    rows={2}
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="e.g. Keep it simple with real-life examples."
                    className="w-full resize-y rounded-md border border-border bg-white px-3 py-2 text-[13px] text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                </Field>
              </div>

              <Button
                variant="primary"
                size="lg"
                className="mt-6 w-full"
                onClick={() => setDialogOpen(true)}
                disabled={!canSubmit}
              >
                Generate with AI <ArrowRight size={14} />
              </Button>

              {!canSubmit && (
                <p className="mt-2 text-[11.5px] text-fg-subtle">
                  Pick (or type) class, subject, chapter, and at least one topic
                  to continue.
                </p>
              )}

              {/* ── Start from scratch (no AI) ─────────────────────────── */}
              <div className="mt-7 border-t border-border-subtle pt-5">
                <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-fg-muted">
                  <PencilRuler size={14} />
                  Start from scratch
                </div>
                <p className="mt-1.5 text-[12px] text-fg-muted">
                  Create an empty pack and build the slides yourself in the
                  editor — no AI generation. Needs class, subject and a title
                  (chapter/topics optional).
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    value={blankTitle}
                    onChange={(e) => setBlankTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') createBlank();
                    }}
                    placeholder={
                      input.chapter
                        ? `e.g. ${input.chapter}`
                        : 'Pack title, e.g. The Human Heart'
                    }
                    className="h-10 flex-1 rounded-md border border-border bg-white px-3 text-[13.5px] text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                  />
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={createBlank}
                    disabled={!canCreateBlank || blankBusy}
                  >
                    {blankBusy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <PencilRuler size={14} />
                    )}
                    Create blank pack
                  </Button>
                </div>
                {blankError && (
                  <p className="mt-2 text-[11.5px] text-red-600">{blankError}</p>
                )}
              </div>
            </section>
          </div>
        </div>

        <GenerateLessonPackDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          defaults={input}
          onComplete={(packId) => {
            setDialogOpen(false);
            navigate(`/lesson-packs/${packId}`);
          }}
        />
      </div>
    </AppShell>
  );
}

/** Inline palette preview under the design-system select. */
function DesignSwatchStrip({ ds }: { ds: DesignSystemSummary | undefined }) {
  if (!ds) return null;
  const swatches = [
    ds.palette.primary,
    ds.palette.secondary,
    ds.palette.accent,
    ds.palette.background,
    ds.palette.surface,
    ...(ds.palette.extras ?? []),
  ].filter(Boolean);
  return (
    <span className="mt-1.5 flex items-center gap-1.5">
      {swatches.slice(0, 7).map((c, i) => (
        <span
          key={`${c}-${i}`}
          className="h-4 w-4 rounded-full border border-black/10"
          style={{ background: c }}
          title={c}
        />
      ))}
      {ds.typography.headingFamily && (
        <span className="ml-1 text-[11px] text-fg-subtle">
          {ds.typography.headingFamily}
        </span>
      )}
    </span>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] text-fg-subtle">{hint}</span>
      )}
    </label>
  );
}
